"use client";

import React, { useEffect } from "react";
import { Sidebar } from "@/components/sidebar/sidebar";
import { Header } from "@/components/layout/header";
import { useUIStore } from "@/store/ui-store";
import { useAuthStore } from "@/store/auth-store";
import "@/lib/theme-debug";

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { theme } = useUIStore();
  const { isAuthenticated } = useAuthStore();

  // Initialize theme on mount
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing dark class first
    root.classList.remove("dark");

    if (theme === "dark") {
      root.classList.add("dark");
    }
  }, [theme]);

  // Handle window resize and set initial sidebar state
  useEffect(() => {
    const handleResize = () => {
      // On mobile, close sidebar by default
      if (window.innerWidth < 768) {
        useUIStore.getState().setSidebarOpen(false);
      }
      // On desktop, keep current state or open by default on first load
      else {
        const currentState = useUIStore.getState().sidebarOpen;
        // Only set to true if this is initial load (no stored state)
        if (currentState === undefined || currentState === null) {
          useUIStore.getState().setSidebarOpen(true);
        }
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Run on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex h-screen bg-slate-950">
      {/* Only show sidebar if user is authenticated */}
      {isAuthenticated && <Sidebar />}

      <div className="flex flex-1 flex-col min-w-0">
        <Header />

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}